import { toPhpLangId } from './php-langid';
import {
   type Country,
   type CountryCode,
   type UserLocaleCode,
   countryByCode,
   parseUserLocale,
} from './user-locale';
import { isEUCountryCode } from '@ifixit/utils';

/**
 * The best-matching user locale for a geo-detected country, null when the
 * country is unknown. `preferredLanguages` breaks ties for multi-locale
 * countries (e.g. Switzerland).
 */
export function userLocaleFromCountryCode(
   countryCode: string | null | undefined,
   preferredLanguages: readonly string[] = []
): UserLocaleCode | null {
   const normalizedCountryCode = normalizeCountryCode(countryCode);
   if (!normalizedCountryCode) return null;
   const country = countryByCode[normalizedCountryCode as CountryCode];
   if (!country) return null;
   return matchLanguage(country, preferredLanguages);
}

/**
 * The geo user-locale signal for a Carpenter page, preferring the page's
 * language. EU countries without a locale in that language share the
 * pan-European store (en-EU).
 */
export function geoUserLocaleForPhpLangId(
   countryCode: string | null | undefined,
   phpLangId: string
): UserLocaleCode | null {
   const userLocale = userLocaleFromCountryCode(countryCode, [phpLangId]);
   if (userLocale == null) return null;
   if (parseUserLocale(userLocale).language === phpLangId) return userLocale;
   if (countryCode != null && isEUCountryCode(countryCode)) return 'en-EU';
   return userLocale;
}

function matchLanguage(country: Country, preferredLanguages: readonly string[]): UserLocaleCode {
   for (const preferredLang of preferredLanguages) {
      const exactMatch = country.locales.find(
         locale => locale.toLowerCase() === preferredLang.toLowerCase()
      );
      if (exactMatch) return exactMatch;

      const langPrefix = toPhpLangId(preferredLang.split('-', 1)[0].toLowerCase());
      const prefixMatch = country.locales.find(
         locale => parseUserLocale(locale).language.toLowerCase() === langPrefix
      );
      if (prefixMatch) return prefixMatch;
   }

   return country.locales[0];
}

function normalizeCountryCode(countryCode: string | null | undefined): string | null {
   if (countryCode == null) {
      return null;
   }

   const normalized = countryCode.trim().toUpperCase();
   return normalized === '' ? null : normalized;
}
